import { TransactionDetails, TransactionListItem } from '@manigo/manigo-domain-typings';

import {
    optionalTransactionsScopeFilters,
    TransactionScopeFilter,
    TransactionFilter,
    transactionFilters,
} from 'models/domain/transactions';


export type TransactionCounterparty = {
    name?: string | null;
    isExternal?: boolean | null;
    type?: string | null;
    mcc?: number | string | null;
    mccDescription?: string | null;
    location?: {
        city?: string | null;
        country?: string | null;
    } | null;
};

type TransactionWithCounterparties = {
    sender?: TransactionCounterparty | null;
    receiver?: TransactionCounterparty | null;
};

export type MerchantSource = 'sender' | 'receiver';

export type TransactionMerchantResult = {
    merchant: TransactionCounterparty | null;
    source: MerchantSource | null;
};

const sanitizeMcc = (mcc?: number | string | null): string | null => {
    if (mcc === null || mcc === undefined) {
        return null;
    }

    const value = typeof mcc === 'number' ? mcc.toString() : `${mcc}`;

    if (!value) {
        return null;
    }

    const trimmedValue = value.trim();

    if (!trimmedValue) {
        return null;
    }

    return trimmedValue.padStart(4, '0');
};

export const getMerchantCategoryCode = (
    mcc?: number | string | null,
): string | undefined => sanitizeMcc(mcc) ?? undefined;

export const getMerchantLocation = (
    merchant?: TransactionCounterparty | null,
): string | undefined => {
    const country = merchant?.location?.country;

    if (!country) {
        return undefined;
    }

    if (country) {
        return `${country}`;
    }

    return country || undefined;
};

const isExternalCounterparty = (counterparty?: TransactionCounterparty | null): boolean => {
    if (!counterparty) {
        return false;
    }

    if (counterparty.isExternal) {
        return true;
    }

    const normalizedType = counterparty.type?.toLowerCase();
    return normalizedType === 'external';
};

export const getTransactionMerchant = <
    T extends TransactionWithCounterparties | null | undefined,
>(transaction: T): TransactionMerchantResult => {
    if (!transaction) {
        return { merchant: null, source: null };
    }

    if (isExternalCounterparty(transaction.receiver)) {
        return { merchant: transaction.receiver ?? null, source: 'receiver' };
    }

    if (isExternalCounterparty(transaction.sender)) {
        return { merchant: transaction.sender ?? null, source: 'sender' };
    }

    return { merchant: null, source: null };
};


export const createScopeFilters = (t, queryParams): TransactionScopeFilter[] => {
    const scopeFilters = transactionFilters.reduce((acc: TransactionFilter[] | any, filterOption: TransactionFilter) => {
        if (filterOption
            && optionalTransactionsScopeFilters.includes(filterOption.name)
            && !queryParams?.showCardVerification
        ) {
            return acc;
        }

        if (filterOption) {
            return [
                ...acc,
                {
                    label: t(`transactions:transactionScopes.${filterOption.transactionScope?.toLowerCase()}.text`) || filterOption.label,
                    value: filterOption.name,
                },
            ];
        }

        return acc;
    }, []);

    return scopeFilters || undefined;
};


export const mapTransactionScopeFilterValueToQuery = (selectedType): {
    transactionType?: TransactionFilter['transactionType'],
    transactionScope?: TransactionFilter['transactionScope']
} => {
    if (selectedType) {
        const selectedOption = transactionFilters.find((filterOption) => (filterOption.name === selectedType));

        if (selectedOption) {
            return {
                transactionType: selectedOption?.transactionType,
                transactionScope: selectedOption?.transactionScope,
            };
        }
    }

    return {};
};


export const isIncomingTransaction = (entityDetailsData: TransactionDetails | TransactionListItem): boolean => {
    const key = `${entityDetailsData.transactionType}_${entityDetailsData.transactionScope}`;
    const mapping = {
        EXT_IBT: true,
        EXT_CT: true,
        EXT_APM: true,
        INT_IP2P: true,
        INT_AC: true,
        INT_EXT: true,
        CT_CR: true,
        FEE_C: true,
        CT_POC: true,


        EXT_OBT: false,
        INT_OP2P: false,
        INT_AD: false,
        INT_EXF: false,
        CT_ATM: false,
        CT_POS: false,
        FEE_D: false,
        EXT_CTR: false,
        CT_POD: false,
    };

    if (key in mapping) {
        return mapping[key];
    }

    return false;

};


export default {
    mapTransactionScopeFilterValueToQuery,
    getTransactionMerchant,
    getMerchantCategoryCode,
    getMerchantLocation,
};
